import "./NotificationPanel.css";

import { createPortal } from "react-dom";

import { CheckCircleDarkIcon, SortArrowsDarkIcon } from "../../assets/icons";
import { LoadingSpinner } from "../../widget";
import { Toggle } from "../../widget/forms/Input/Toggle";
import { FlyoutPanel } from "../../widget/general/FlyoutPanel";
import { NotificationCard, NotificationCardVariant } from "./components/NotificationCard";
import { useNotificationPanel } from "./useNotificationPanel";

export interface NotificationPanelProps {
  show: boolean;
  onClose: () => void;
  variant?: NotificationCardVariant;
}

export const NotificationPanel = ({ show, onClose, variant = "hub" }: NotificationPanelProps): JSX.Element | null => {
  const {
    items,
    hasUnread,
    showDismissed,
    sortOrder,
    isFetchingMore,
    contentAreaRef,
    handleDismiss,
    handleNotificationClick,
    handleMarkAllRead,
    handleToggleShowDismissed,
    handleToggleSortOrder,
    onScrollContent,
  } = useNotificationPanel({ show });

  const content = (
    <div className="NotificationPanelContent">
      <div className="NotificationPanelFilters">
        <Toggle value={showDismissed} label="Show dismissed" size="medium" onChange={handleToggleShowDismissed} />
        <div className="NotificationPanelFilterActions">
          {hasUnread && (
            <button
              className="NotificationPanelIconButton"
              onClick={handleMarkAllRead}
              aria-label="Mark all as read"
              title="Mark all as read"
            >
              <CheckCircleDarkIcon />
            </button>
          )}
          <button
            className="NotificationPanelIconButton"
            onClick={handleToggleSortOrder}
            aria-label={sortOrder === "newest" ? "Sort oldest first" : "Sort newest first"}
            title={sortOrder === "newest" ? "Sort oldest first" : "Sort newest first"}
          >
            <SortArrowsDarkIcon />
          </button>
        </div>
      </div>
      {items.length > 0 ? (
        <div className="NotificationList">
          {items.map((item) => (
            <NotificationCard
              key={item.uuid}
              text={item.text}
              date={item.date}
              raisedBy={item.raisedBy}
              userInitials={item.userInitials}
              unread={item.unread}
              dismissed={item.dismissed}
              variant={variant}
              onDismiss={() => handleDismiss(item.uuid)}
              onClick={() => handleNotificationClick(item.uuid)}
            />
          ))}
          {isFetchingMore && (
            <div className="NotificationList_loading">
              <LoadingSpinner type="dark" />
            </div>
          )}
        </div>
      ) : (
        <p className="body1 NotificationPanelEmpty">You have no notifications</p>
      )}
    </div>
  );

  /* The bell that owns this panel sits inside the fixed, z-indexed navigation bar, which is its own
     stacking context — the panel's z-index would then only order it against the bar's own children,
     leaving page content (sticky table headers, toolbars) painting over it. Portalling to the body
     puts the panel back in the root stacking context where its z-index counts. */
  return createPortal(
    <FlyoutPanel
      show={show}
      onClose={onClose}
      size="ExtraSmall"
      height="Full"
      header="Notifications"
      buttonsChildren={undefined}
      contentChildren={content}
      className={`notification-panel--${variant}`}
      onScrollContent={onScrollContent}
      contentAreaRef={contentAreaRef}
    />,
    document.body
  );
};
